export const FINANCE_BUDGET_MAX_FILE_BYTES = 25 * 1024 * 1024;
export const FINANCE_BUDGET_MARGIN_BPS_MIN = -1_000_000;
export const FINANCE_BUDGET_MARGIN_BPS_MAX = 10_000;

export function isFinanceBudgetMarginBps(value: number | null | undefined): boolean {
  return value == null || (
    Number.isInteger(value)
    && value >= FINANCE_BUDGET_MARGIN_BPS_MIN
    && value <= FINANCE_BUDGET_MARGIN_BPS_MAX
  );
}
export const FINANCE_BUDGET_SUPPORTED_MIME_TYPES = [
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/pdf",
] as const;

export type FinanceBudgetSupportedMime = typeof FINANCE_BUDGET_SUPPORTED_MIME_TYPES[number];
export type FinanceBudgetExtractionStatus =
  | "processing"
  | "review_required"
  | "confirmed"
  | "failed"
  | "superseded";

export type FinanceBudgetLineDraft = {
  lineNumber: number;
  productCatalogId?: number | null;
  productName: string;
  costCategory?: "materials" | "labor" | "other" | null;
  description?: string | null;
  reference?: string | null;
  unit: string;
  quantity: number;
  saleUnitPriceCents?: number | null;
  saleTotalCents: number;
  estimatedUnitCostCents?: number | null;
  estimatedCostCents?: number | null;
  expectedMarginCents?: number | null;
  expectedMarginBps?: number | null;
  extractionConfidenceBps?: number | null;
  rawText?: string | null;
  reviewStatus: "review_required" | "confirmed";
  warnings: string[];
};

export type FinanceBudgetTotals = {
  sourceBaseCents: number;
  vatRate: number;
  vatCents: number;
  grossCents: number;
  estimatedCostCents: number | null;
  expectedMarginCents: number | null;
  expectedMarginBps: number | null;
};

export type FinanceBudgetMargins = {
  budgetExpectedMarginCents: number | null;
  budgetExpectedMarginBps: number | null;
  budgetActualMarginCents: number;
  budgetActualMarginBps: number | null;
  contractualActualMarginCents: number | null;
  contractualActualMarginBps: number | null;
  /** @deprecated Use budgetActualMarginCents. */
  actualMarginCents: number;
};

function assertSafeInteger(value: number, name: string): void {
  if (!Number.isSafeInteger(value)) throw new Error(`${name}_MUST_BE_SAFE_INTEGER_CENTS`);
}

function marginBps(marginCents: number | null, denominatorCents: number | null): number | null {
  if (marginCents === null || denominatorCents === null || denominatorCents === 0) return null;
  return Math.round((marginCents / denominatorCents) * 10_000);
}

export function calculateBudgetMargins(input: {
  sourceBaseCents: number;
  estimatedCostCents: number | null;
  actualCostCents: number;
  effectiveContractAmountCents: number | null;
}): FinanceBudgetMargins {
  const budgetExpectedMarginCents = input.estimatedCostCents === null
    ? null
    : input.sourceBaseCents - input.estimatedCostCents;
  const budgetActualMarginCents = input.sourceBaseCents - input.actualCostCents;
  const contractualActualMarginCents = input.effectiveContractAmountCents === null
    ? null
    : input.effectiveContractAmountCents - input.actualCostCents;
  return {
    budgetExpectedMarginCents,
    budgetExpectedMarginBps: marginBps(budgetExpectedMarginCents, input.sourceBaseCents),
    budgetActualMarginCents,
    budgetActualMarginBps: marginBps(budgetActualMarginCents, input.sourceBaseCents),
    contractualActualMarginCents,
    contractualActualMarginBps: marginBps(contractualActualMarginCents, input.effectiveContractAmountCents),
    actualMarginCents: budgetActualMarginCents,
  };
}

export function normalizeBudgetReference(value: string | null | undefined): string {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
}

export function calculateBudgetLine(input: FinanceBudgetLineDraft): FinanceBudgetLineDraft {
  if (!Number.isFinite(input.quantity) || input.quantity <= 0) {
    throw new Error("BUDGET_LINE_QUANTITY_MUST_BE_POSITIVE");
  }
  assertSafeInteger(input.saleTotalCents, "SALE_TOTAL");
  if (input.saleTotalCents < 0) throw new Error("SALE_TOTAL_MUST_BE_NONNEGATIVE");

  const estimatedCostCents = input.estimatedCostCents
    ?? (input.estimatedUnitCostCents == null
      ? null
      : Math.round(input.estimatedUnitCostCents * input.quantity));
  if (estimatedCostCents !== null) {
    assertSafeInteger(estimatedCostCents, "ESTIMATED_COST");
    if (estimatedCostCents < 0) throw new Error("ESTIMATED_COST_MUST_BE_NONNEGATIVE");
  }

  const expectedMarginCents = estimatedCostCents === null
    ? null
    : input.saleTotalCents - estimatedCostCents;
  const expectedMarginBps = expectedMarginCents === null || input.saleTotalCents === 0
    ? null
    : Math.round((expectedMarginCents / input.saleTotalCents) * 10_000);

  return {
    ...input,
    estimatedCostCents,
    expectedMarginCents,
    expectedMarginBps,
  };
}

export function calculateBudgetTotals(lines: FinanceBudgetLineDraft[], vatRate: number): FinanceBudgetTotals {
  if (!Number.isFinite(vatRate) || vatRate < 0 || vatRate > 100) {
    throw new Error("VAT_RATE_OUT_OF_RANGE");
  }
  const calculated = lines.map(calculateBudgetLine);
  const sourceBaseCents = calculated.reduce((sum, line) => sum + line.saleTotalCents, 0);
  assertSafeInteger(sourceBaseCents, "SOURCE_BASE");

  const hasPendingCost = calculated.some((line) => line.estimatedCostCents === null);
  const estimatedCostCents = hasPendingCost
    ? null
    : calculated.reduce((sum, line) => sum + (line.estimatedCostCents ?? 0), 0);
  const expectedMarginCents = estimatedCostCents === null ? null : sourceBaseCents - estimatedCostCents;
  const expectedMarginBps = expectedMarginCents === null || sourceBaseCents === 0
    ? null
    : Math.round((expectedMarginCents / sourceBaseCents) * 10_000);
  const vatCents = Math.round(sourceBaseCents * vatRate / 100);
  const grossCents = sourceBaseCents + vatCents;

  return {
    sourceBaseCents,
    vatRate,
    vatCents,
    grossCents,
    estimatedCostCents,
    expectedMarginCents,
    expectedMarginBps,
  };
}

export type BudgetComparisonLine = {
  id: number;
  productCatalogId: number | null;
  reference: string | null;
  quantity: number;
  estimatedCostCents: number | null;
};

export type ActualPurchaseLine = {
  invoiceLineId: number;
  productCatalogId: number | null;
  sku: string | null;
  quantity: number;
  actualCostCents: number;
};

export type BudgetActualComparison = {
  budgetLineId: number;
  matchType: "product_catalog" | "sku" | "unmatched";
  purchasedQuantity: number;
  actualCostCents: number;
  deviationCents: number | null;
  status: "pending" | "partial" | "complete" | "exceeded" | "unmatched";
  invoiceLineIds: number[];
};

/**
 * Deterministic, non-destructive matching. Every supplier line can contribute
 * to at most one budget line; approximate text matching is deliberately absent.
 */
export function compareBudgetLinesWithPurchases(
  budgetLines: BudgetComparisonLine[],
  purchases: ActualPurchaseLine[],
): BudgetActualComparison[] {
  const consumed = new Set<number>();
  return budgetLines.map((budgetLine) => {
    const productMatches = budgetLine.productCatalogId === null
      ? []
      : purchases.filter((purchase) => (
        !consumed.has(purchase.invoiceLineId)
        && purchase.productCatalogId === budgetLine.productCatalogId
      ));
    const normalizedReference = normalizeBudgetReference(budgetLine.reference);
    const skuMatches = productMatches.length || !normalizedReference
      ? []
      : purchases.filter((purchase) => (
        !consumed.has(purchase.invoiceLineId)
        && normalizeBudgetReference(purchase.sku) === normalizedReference
      ));
    const matches = productMatches.length ? productMatches : skuMatches;
    for (const match of matches) consumed.add(match.invoiceLineId);

    const purchasedQuantity = matches.reduce((sum, match) => sum + match.quantity, 0);
    const actualCostCents = matches.reduce((sum, match) => sum + match.actualCostCents, 0);
    const deviationCents = budgetLine.estimatedCostCents === null
      ? null
      : actualCostCents - budgetLine.estimatedCostCents;
    let status: BudgetActualComparison["status"] = "pending";
    if (!matches.length) status = normalizedReference || budgetLine.productCatalogId ? "unmatched" : "pending";
    else if (purchasedQuantity < budgetLine.quantity) status = "partial";
    else if (purchasedQuantity > budgetLine.quantity) status = "exceeded";
    else status = "complete";

    return {
      budgetLineId: budgetLine.id,
      matchType: productMatches.length ? "product_catalog" : skuMatches.length ? "sku" : "unmatched",
      purchasedQuantity,
      actualCostCents,
      deviationCents,
      status,
      invoiceLineIds: matches.map((match) => match.invoiceLineId),
    };
  });
}

export const FINANCE_BUDGET_MARKERS = {
  legacyUi: "FINANCE_BUDGET_BASELINES_UI_DEPRECATED",
  canonicalUi: "FINANCE_BUDGET_DOCUMENTS_CANONICAL_UI",
} as const;

/**
 * Public Finance Budget Document DTO projection.
 *
 * Internal storage/security columns (source object keys, content hashes,
 * soft-delete markers and acting user ids) are NEVER serialized into public
 * API responses. We project from an explicit allowlist so forbidden keys are
 * absent (not nulled) regardless of what the storage row contains. The same
 * helper set is reused by the server serializer and by behaviour tests.
 */
export const FINANCE_BUDGET_FORBIDDEN_PUBLIC_KEYS = [
  "sourceObjectKey",
  "sourceSha256",
  "deletedAt",
  "createdById",
  "confirmedById",
] as const;

export const FINANCE_BUDGET_FORBIDDEN_PUBLIC_KEY_PATTERNS = [
  /objectKey/i,
  /storageKey/i,
  /sourceSha256/i,
] as const;

export const PUBLIC_BUDGET_DOCUMENT_FIELDS = [
  "id",
  "installationId",
  "version",
  "current",
  "reference",
  "budgetDate",
  "sourceFileName",
  "sourceMimeType",
  "extractionStatus",
  "extractionWarnings",
  "sourceClientName",
  "sourceClientTaxId",
  "sourceAddress",
  "notes",
  "sourceBaseCents",
  "vatRate",
  "vatCents",
  "grossCents",
  "estimatedCostCents",
  "expectedMarginCents",
  "expectedMarginBps",
  "confirmedAt",
  "createdAt",
  "updatedAt",
  "supersededAt",
] as const;

export type FinanceBudgetPublicField = (typeof PUBLIC_BUDGET_DOCUMENT_FIELDS)[number];

/**
 * Static structural type for the public budget document DTO. Field types mirror
 * the storage row so downstream consumers (server serializers, tRPC clients)
 * keep concrete types; forbidden internal columns are simply absent.
 */
export type FinanceBudgetPublicDocument = {
  id: number;
  installationId: number;
  version: number;
  current: boolean;
  reference: string;
  budgetDate: string;
  sourceFileName: string | null;
  sourceMimeType: string | null;
  extractionStatus: FinanceBudgetExtractionStatus;
  extractionWarnings: string[];
  sourceClientName: string | null;
  sourceClientTaxId: string | null;
  sourceAddress: string | null;
  notes: string | null;
  sourceBaseCents: number;
  vatRate: string;
  vatCents: number;
  grossCents: number;
  estimatedCostCents: number | null;
  expectedMarginCents: number | null;
  expectedMarginBps: number | null;
  confirmedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
  supersededAt: Date | null;
};

/**
 * Projects a raw finance budget storage row onto the public DTO by copying
 * only the allowlisted fields. Forbidden internal columns are dropped entirely
 * (never nulled). The return type preserves the input's concrete field types
 * for the allowlisted keys so type-safe callers keep their types. Callers keep
 * using the full row for internal calculations (e.g. getBudgetSourceBuffer
 * still reads sourceObjectKey directly).
 */
export function pickPublicBudgetFields<T extends object>(
  budget: T,
): { [K in keyof T & FinanceBudgetPublicField]: T[K] } {
  const source = budget as unknown as Record<string, unknown>;
  const result: Record<string, unknown> = {};
  for (const field of PUBLIC_BUDGET_DOCUMENT_FIELDS) {
    if (field in source) {
      result[field] = source[field];
    }
  }
  return result as { [K in keyof T & FinanceBudgetPublicField]: T[K] };
}

/**
 * Recursively walks a public response payload and throws when any forbidden
 * key name (exact or pattern-matched) is present anywhere in the tree. Used as
 * a defence-in-depth guard and by behaviour tests to assert no internal
 * storage/security key leaks out of getById/list/preview/confirm/comparison.
 */
export function assertNoForbiddenBudgetPublicKeys(payload: unknown, path = "$"): void {
  if (payload === null || typeof payload !== "object") return;
  if (Array.isArray(payload)) {
    payload.forEach((item, index) => assertNoForbiddenBudgetPublicKeys(item, `${path}[${index}]`));
    return;
  }
  for (const [key, value] of Object.entries(payload as Record<string, unknown>)) {
    const keyPath = `${path}.${key}`;
    if ((FINANCE_BUDGET_FORBIDDEN_PUBLIC_KEYS as readonly string[]).includes(key)) {
      throw new Error(`FORBIDDEN_BUDGET_PUBLIC_KEY_EXPOSED:${keyPath}`);
    }
    if (FINANCE_BUDGET_FORBIDDEN_PUBLIC_KEY_PATTERNS.some((pattern) => pattern.test(key))) {
      throw new Error(`FORBIDDEN_BUDGET_PUBLIC_KEY_PATTERN_EXPOSED:${keyPath}`);
    }
    assertNoForbiddenBudgetPublicKeys(value, keyPath);
  }
}
